/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { counterOnlyParam } from '../../constants/fields';

import { updateError } from './user';

import { dispatch, store } from '../store';
import { getPracticeId } from './clincheck';

// All Practices phase-2 consumer: if the user has a multi-practice
// selection, every widget count aggregates across those practices so
// the dashboard badges match the lead-table rows below them.
// Returns '' for single-practice — the URL's practiceId already scopes.
const _activePracticesSuffix = () =>
    practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds);


const initialState = {
    isLoading: false,
    widgetCounter: [],
    isLoadingWidgetCounter: true,
    // Track which widget keys have finished loading (for skeleton → loaded transition)
    loadedKeys: [],
    // Total number of widgets expected (set when reload starts)
    expectedCount: 0,
};

const slice = createSlice({
    name: 'widgetCounter',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        setWidgetCounter(state, action) {
            state.widgetCounter = action.payload;
        },
        upsertWidgetCounter(state, action) {
            const { key, ...rest } = action.payload;
            const idx = state.widgetCounter.findIndex(item => item.key === key);
            if (idx !== -1) {
                state.widgetCounter[idx] = { ...state.widgetCounter[idx], ...rest };
            } else {
                state.widgetCounter.push({ key, ...rest });
            }
            // Mark this key as loaded
            if (!state.loadedKeys.includes(key)) {
                state.loadedKeys.push(key);
            }
        },
        // Called when a new widget reload cycle starts
        startWidgetLoading(state, action) {
            // Only reset loadedKeys on first load (when no data exists yet).
            // On subsequent reloads (user actions), keep existing data visible
            // while new data loads in the background.
            if (state.widgetCounter.length === 0) {
                state.loadedKeys = [];
            }
            state.expectedCount = action.payload || 0;
        },

    }
});

export default slice.reducer;

export const { setWidgetCounter, upsertWidgetCounter, reset, startWidgetLoading } = slice.actions;

export const getDateRange = (date) => {
    const filteredDate = date.split(',');
    const customStartDate = `${filteredDate[0]} 00:00:00`;
    const customEndDate = `${filteredDate[1]} 23:59:00`;
    return `${customStartDate},${customEndDate}`;
};

export const getQueryTask = (data) => {
    const { isTasksCompleted, assigneeUser, unassignedUser, filter } = data.taskFilter
    let query = `?lead__is_archived=false&ordering=datetime&is_completed=${isTasksCompleted}`;

    if (filter === 'TODO') {
        query += `&group__in=TODO`
    } else if (filter === "CALLBACK") {
        query += `&group__in=CALLBACK_REQUEST`
    } else {
        query += `&group__in=TODO,FOLLOW_UP,CALLBACK_REQUEST`
    }
    if (assigneeUser) {
        query += `&assignees__user__id__in=${assigneeUser}`
    }
    if (unassignedUser) {
        query += '&assignees__isnull=true'
    }
    if (filter != null && filter !== 'TODO' && filter !== 'leadNotes' && filter !== '' && filter !== "CALLBACK" && filter !== 'UNANSWERED' && filter !== 'undefined') {
        query += `&lead_treatment__status=${filter}`
    }
    if (data.dateFilters === 'Upcoming') {
        query += `&datetime__date__gte=${moment(new Date().setDate(new Date().getDate() + 1)).format('YYYY-MM-DD')}`
    } else if (data.dateFilters === 'Today') {
        query += `&datetime__date__lte=${moment(new Date().setDate(new Date().getDate())).format('YYYY-MM-DD')}`
    }

    if (data.treatment) {
        query += `&lead_treatment__treatment__in=${data.treatment}`
    }

    return query;

}

export const getQuery = (data) => {
    let query = '?is_archived=false';

    if (data?.dateRange) {
        query += `&created_at__range=${getDateRange(data?.dateRange)}`;
    }
    if (data.treatmentStatusForPrice === 'TREATMENT_STARTED' || data.status === 'TREATMENT_STARTED') {
        if (data.startDate && data.endDate) {
            query += `&state_transition_params={"state":"TREATMENT_STARTED","timestamp_gte":"${data.startDate} 00:00:00","timestamp_lte":"${data.endDate} 23:59:00"}`;
        }
        // else if (!data.appointmentDateRange) {
        //     query += '&state_transition_params={"state":"TREATMENT_STARTED"}';
        // }
    }
    if (data?.status) {
        if (data?.status === "NEW" && data?.healthCareProvider && data?.hasNewOrNullTreatment) {
            query += `&has_new_or_null_treatment=true`
        } else
            query += `&lead_treatments__status=${data?.status}`;
    }
    if (data?.practiceArray?.length) {
        query += `&practice__in=${data?.practiceArray}`;
    }
    if (data?.treatmentStatus && data.treatmentStatus !== 'undefined') {
        query += `&lead_treatment__status=${data.treatmentStatus}`;
    }
    if (data?.treatment) {
        query += `&lead_treatments__treatment__in=${data?.treatment}`;
    }
    if (data?.reminderContactedCount) {
        query += '&contacted_count=0';
    }
    if (data?.contactedCount && !Number.isNaN(Number(data.contactedCount))) {
        query += `&lead_treatments__lead_contacted_counts__count__gte=${Number(data.contactedCount)}`;
    }
    if (data?.contactedCountForStatus) {
        query += `&contacted_count_for_status=${data.contactedCountForStatus}`;
    }
    if (data?.clinCheckStatus) {
        query += `&lead_treatments__leadtreatment_clincheck__status__in=${data?.clinCheckStatus}`;
    }
    if (data?.isLeadNotes) {
        query += '&latest_note__in=AWAITING_RESPONSE';
    }
    if (data.appointmentDateRange) {
        query += `&lead_treatments__appointments__datetime__range=${getDateRange(data.appointmentDateRange)}`;
    }
    // Upcoming this week - appointments in the next 7 days
    if (data.upcomingThisWeek) {
        const today = moment().format('YYYY-MM-DD');
        const nextWeek = moment().add(7, 'days').format('YYYY-MM-DD');
        query += `&lead_treatments__appointments__datetime__range=${today} 00:00:00,${nextWeek} 23:59:00`;
    }
    // Past appointments with no outcome recorded (BOOKED status but datetime in the past)
    if (data.pastNoOutcome) {
        const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
        query += `&lead_treatments__appointments__datetime__lte=${yesterday} 23:59:00`;
    }
    if (data.treatmentPrice) {
        query += '&aggregate[Sum]=lead_treatments__custom_price';
    }
    if (data.isContactedCountNull) {
        query += '&lead_treatments__lead_contacted_counts__isnull=true';
    }
    if (data.isClinCheckTable) {
        query += '&lead_treatments__leadtreatment_clincheck__isnull=false';
    }
    if (data.group__in && !data.pendingWhatsapp) {
        query += '&group__in=FOLLOW_UP,TODO,NEW_LEAD';
        query += '&lead__is_archived=false';
    }
    if (data.completedFilter && data.is_completed !== undefined && data.is_completed !== null) {
        query += `&is_completed=${data.is_completed}`;
    }
    if (data.isTaskDateTimeRange && data.taskDateRange) {
        query += `&datetime__date__range=${data?.taskDateRange}`;
    } else if (data.dateFilters === 'Today') {
        query += `&datetime__date__lte=${moment(new Date()).format('YYYY-MM-DD')}`;
    } else if (data.dateFilters === 'Upcoming') {
        query += `&datetime__date__gte=${moment(new Date()).add(1, 'day').format('YYYY-MM-DD')}`;
    }
    if (data.treatmentStatusForPrice) {
        query += `&lead_treatments__status__in=${data?.treatmentStatusForPrice}`;
    }
    if (data.appStatus) {
        query += `&lead_treatments__appointments__status=${data?.appStatus}`;
    }
    if (data.appStatusForPrice) {
        query += `&lead_treatments__appointments__status__in=${data?.appStatusForPrice}`;
    }
    if (data?.healthCareProvider) {
        query += `&healthcare_provider=${data?.healthCareProvider}`;
    }
    if (data?.hasNewOrNullTreatment) {
        query += `&has_new_or_null_treatment=${data?.hasNewOrNullTreatment}`;
    }

    return query;
};

export const setCounter = (data, counter, teethCount) => {
    dispatch(upsertWidgetCounter({ key: data.key, count: counter, teethCount }));
};


export function getWidgetCounter(data) {
    return async () => {
        try {
            const { type } = data ?? {};
            const isTreatmentV2Enabled = type === "TREATMENT_STARTED";
            let query = getQuery(data);
            if (isTreatmentV2Enabled) {
                query = query.replaceAll("lead_treatments__", "");
                query = `${query}&lead__healthcare_provider=PRIVATE`
            }

            let teethCount = 0;

            const response = data.acrossPractice ? await callApi(`/api/v1/leads-flat/${query}&${counterOnlyParam}`) : isTreatmentV2Enabled ?
                await callApi(`/api/v1/practices/${getPracticeId()}/lead-treatments-flat/${query}&fields={leadtreatment_specification}`) :
                await callApi(`/api/v1/practices/${getPracticeId()}/leads/${query}&${counterOnlyParam}${_activePracticesSuffix()}`);
            if (isTreatmentV2Enabled) {
                teethCount = (response?.data?.results ?? [])?.reduce((sum, item) => sum + (item?.leadtreatment_specification?.teeth_count || 0), 0) ?? 0;
            }
            if (response.status === 200) {
                const result = response.data;
                setCounter(data, data.isTreatmentNotPresent ? 0 : result?.count, teethCount);

            } else {
                dispatch(updateError({ success: false, message: response.data || 'Something went wrong' }));
            }

        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}
export function getTreatmentWidgetPrice(data) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {

            const response = await callApi(`/api/v1/practices/${getPracticeId()}/reports/leads/${getQuery(data)}${_activePracticesSuffix()}`);

            if (response.status === 200) {
                const result = response.data;
                setCounter(data, result?.data?.sum_lead_treatments__custom_price);

            } else {
                dispatch(updateError({ success: false, message: response.data || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}
export function getTaskWidgetCounter(data) {
    return async () => {
        try {

            const response = await callApi(`/api/v1/practices/${getPracticeId()}/reminders/${data?.taskFilter ? getQueryTask(data) : getQuery(data)}&${counterOnlyParam}`);

            if (response.status === 200) {
                const result = response.data;
                setCounter(data, result?.count);
            } else {
                dispatch(updateError({ success: false, message: response.data || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

